import React, { createContext, useContext, useCallback, useState } from 'react'

interface CarouselContextType {
  currentImage: number
  setCurrentImage: (index: number) => void
  setCurrentImageProgrammatically: (index: number) => void
  totalImages: number
  imageSize: number
  setTotalImages: (total: number) => void
  lastProgrammaticChange: number
  isFinite: boolean
}

const CarouselContext = createContext<CarouselContextType | undefined>(
  undefined
)

const useCarousel = () => {
  const context = useContext(CarouselContext)
  if (!context) {
    throw new Error('useCarousel must be used within a CarouselProvider')
  }
  return context
}

interface CarouselProviderProps {
  children: React.ReactNode
  imageSize?: number
  initialImageIndex?: number
  onImageChange?: (index: number) => void
  isFinite?: boolean
}

const CarouselProvider: React.FC<CarouselProviderProps> = ({
  children,
  imageSize = 80,
  initialImageIndex = 0,
  onImageChange,
  isFinite = false,
}) => {
  const [currentImage, setCurrentImageState] = useState(initialImageIndex)
  const [totalImages, setTotalImages] = useState(0)
  const [lastProgrammaticChange, setLastProgrammaticChange] = useState(0)

  const setCurrentImage = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalImages) {
        setCurrentImageState(index)
        onImageChange?.(index)
      }
    },
    [totalImages, onImageChange]
  )

  const setCurrentImageProgrammatically = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalImages) {
        setCurrentImageState(index)
        setLastProgrammaticChange(Date.now()) // Помечаем как программное изменение
        onImageChange?.(index)
      }
    },
    [totalImages, onImageChange]
  )

  const contextValue: CarouselContextType = {
    currentImage,
    setCurrentImage,
    setCurrentImageProgrammatically,
    totalImages,
    imageSize,
    setTotalImages,
    lastProgrammaticChange,
    isFinite,
  }

  return (
    <CarouselContext.Provider value={contextValue}>
      {children}
    </CarouselContext.Provider>
  )
}

export { CarouselProvider, useCarousel }
export type { CarouselContextType, CarouselProviderProps }
